<template>
  <modal
    class="modal"
    :width="550"
  >
    <div class="content">
      <div class="header">
        <div class="header-text">
          <span class="name">
          {{ eventData.name }}
          </span>
            <span>
            Czas zajęć: {{ times.start }} - {{ times.end }}
          </span>
        </div>
        <custom-icon-button
          button-name="close-clients-modal"
          class="icon"
          icon-name="times"
          @closeClientsModalButton="$emit('close-clients-modal', { refresh: smsWasUpdated })"
        />
      </div>
      <div class="list m-0">
        <div
          v-for="client in data.clients.clients"
          :key="client.id"
        >
          <div class="d-flex flex-row gap-4 border-bottom py-1 align-items-center">
            <custom-checkbox
              v-if="canApproveEvent || isAdmin"
              class="col-sm"
              :class="data.clients.is_approved?'disabled':''"
              :disabled="data.clients.is_approved"
              :checked="getState(client)"
              @checkChange="changeCheck(client)"
            />
            <div class="col col-lg-6">
              <span>
                {{ client.surname }} {{ client.name }}
              </span>
              <span  v-if="client?.amount !== null" class="col">
                ({{ client.amount }} zł)
              </span>
            </div>
            <div class="col col-lg-4">
              <sms-status-button
                :sms-status="client.sms_status"
                :disabled="data.clients.is_approved"
                @status-change="updateSmsStatus(client.id, $event)"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="w-100 d-flex justify-content-center pt-2 gap-4">
        <custom-button
          v-if="!data.clients.is_approved && (canApproveEvent || isAdmin)"
          class="accept-button presence-color"
          button-name="Zatwierdź obecności"
          :disabled="buttonDisabled"
          @click="confirmPresence"
        />
       <custom-button
          v-if="isAdmin && data.clients.is_approved"
          class="accept-button presence-color"
          button-name="Cofnij płatność"
          :disabled="buttonDisabled"
          @click="rollbackTransaction"
        />
      </div>
      <div class="w-100 d-flex justify-content-center pt-2">
        <p
          class="error-msg"
          v-if="errorMsg"
        >
          {{ errorMsg }}
        </p>
      </div>
    </div>
  </modal>
</template>

<script>
import {
  computed,
  reactive,
  ref,
} from 'vue';

import axios from 'axios';
import dayjs from 'dayjs';

import { urlBuilder } from '@/plugins/helpers';
import store from '@/store';

import Modal from './Modal.vue';
import CustomIconButton from '../common/CustomIconButton.vue';
import CustomCheckbox from '../common/CustomCheckbox.vue';
import CustomButton from '../common/CustomButton.vue';
import SmsStatusButton from '../common/SmsStatusButton.vue';
import messages from '../../consts/messages';

export default {
  name: 'ClientsModal',
  components: {
    CustomButton, CustomCheckbox, CustomIconButton, Modal, SmsStatusButton,
  },
  props: {
    eventData: Object,
  },
  async setup(props, { emit }) {
    const times = computed(() => ({
      start: dayjs(props.eventData.start).format('HH:mm'),
      end: dayjs(props.eventData.end).format('HH:mm'),
    }));

    const data = reactive({
      clients: null,
      presentClients: [],
    });

    async function getClients() {
      data.clients = (await axios.get(urlBuilder(`/events/${props.eventData.id}/`))).data;
      data.presentClients = data.clients.present_clients.map((client) => client.id);
    }

    await getClients();

    function getState(client) {
      return data.presentClients.includes(client.id);
    }

    function changeCheck(client) {
      if (getState(client)) {
        data.presentClients = data.presentClients.filter(
          (clientId) => clientId !== client.id,
        );
      } else {
        data.presentClients.push(client.id);
      }
    }

    const errorMsg = ref('');

    const buttonDisabled = ref(false);

    const smsWasUpdated = ref(false);

    async function confirmPresence() {
      buttonDisabled.value = true;
      const payload = {
        present_clients: data.presentClients,
        charge: true,
      };
      errorMsg.value = '';
      try {
        await axios.patch(urlBuilder(`/events/${props.eventData.id}/`), payload);
      } catch (err) {
        errorMsg.value = messages.something_wrong;
      }
      await getClients();
      await store.dispatch('resetChangeEvent');
      emit('close-clients-modal', { refresh: true });
      buttonDisabled.value = false;
    }

    const isAdmin = computed(() => store.getters.isAdmin);

    async function rollbackTransaction() {
      buttonDisabled.value = true;
      try {
        await axios.get(urlBuilder(`/events/${props.eventData.id}/rollback_transaction/`));
      } catch (err) {
        if (err.status) {
          errorMsg.value = messages.history_is_unavailable;
        } else {
          errorMsg.value = messages.something_wrong;
        }
        buttonDisabled.value = false;

        return;
      }
      await getClients();
      emit('close-clients-modal', { refresh: true });
      buttonDisabled.value = false;
    }

    const canApproveEvent = computed(() => store.getters.canApproveEvent);

    async function updateSmsStatus(clientId, newStatus) {
      try {
        await axios.patch(
          urlBuilder(`/events/${props.eventData.id}/sms-status/`),
          {
            client_id: clientId,
            sms_status: newStatus,
          },
        );
        await getClients();
        smsWasUpdated.value = true;
      } catch (err) {
        errorMsg.value = messages.something_wrong;
      }
    }

    return {
      canApproveEvent,
      buttonDisabled,
      changeCheck,
      confirmPresence,
      data,
      errorMsg,
      getState,
      isAdmin,
      rollbackTransaction,
      smsWasUpdated,
      times,
      updateSmsStatus,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/css/colors.scss';
@import '../../assets/css/errors';

.modal {
  max-height: 100vh;
}

.content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.header {
  border-bottom: 1px solid $grey;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 10px;

  .header-text {
    display: flex;
    flex-direction: column;
    justify-content: left;
  }
}

.list {
  margin-right: 20px;
}

.name {
  font-size: 20px;
  font-weight: bold;
}

.icon {
  border: none;
  color: $grey;
  cursor: pointer;
  height: 20px;
  padding: 0;
  width: 20px;
}

.accept-button {
  background-color: $blue;
  border-radius: 5px;
  border: 1px solid $blue;
  color: $white;
  height: 40px;
  width: 200px;
}

.presence-color {
  background-color: #0FEC13;
  border: 1px solid #0FEC13;
  color: $white;
}
</style>
